import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react'
import { Calendar, Columns3, Download, FileText, Filter, Maximize2, Minimize2, RefreshCw, Search, TableProperties } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { QuickBooksFpaCachePrepareResponse, ServerApiClient } from '../../../data/ServerApiClient'
import {
  DEFAULT_GL_DETAIL_COLUMNS,
  GL_DETAIL_NUMERIC_COLUMNS,
  cacheManifestToPrepareResponse,
  cacheTableIdMatches,
  findGlDetailCacheTable,
  glDetailColumnLabel,
  hydrateGlDetailCacheTable,
  materializeQuickBooksGlDetailTable,
  parseNumberish,
  previewRowsAsObjects,
  trimGlStr,
} from '../quickBooksGlDetailShared'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CfoControlRow, CfoLoadingNote } from './CfoPagePrimitives'
import { CFO_CORE_DUCKDB_DATASET_ID, buildCfoDuckDbOpts } from './cfoDuckDb'
import {
  EXCEL_WORKSHEET_ROW_LIMIT,
  buildCfoGlDetailDistinctValuesSql,
  buildCfoGlDetailRowsSql,
  buildCfoGlDetailTotalsSql,
  formatGlCell,
  formatGlNumber,
  parseCfoGlDetailTotals,
  selectVisibleGlColumns,
  type CfoGlDetailFilterOperator,
  type CfoGlDetailFilterState,
  type CfoGlDetailDistinctValueSort,
  type CfoGlDetailQueryState,
  type CfoGlDetailTotals,
} from './cfoGlDetailQuery'
import {
  buildCfoGlDetailCsvFileName,
  buildCfoGlDetailExcelFileName,
  buildCfoGlDetailExcelSnapshot,
  requiredCfoGlDetailExcelSheetCount,
} from './cfoGlDetailExcelExport'
import { CfoGlProfilePane } from './CfoGlProfilePane'
import {
  cfoGlProfileFiltersLabel,
  cfoGlProfileFiltersToPredicates,
  type CfoGlProfileFilters,
} from './cfoGlProfileModel'
import { clearPendingCfoGlDetailHandoff, peekPendingCfoGlDetailHandoff } from './cfoGlDetailHandoff'
import { CfoGlDetailServerFallback } from './CfoGlDetailServerFallback'
import { useCfoInlineGlDetail } from './CfoGlDetailModal'
import { useCfoAnalysis, type CfoAnalysisGlDetailTarget } from './analysis'
import { CFO_SURFACES } from './cfoTelemetry'
import { useCfoBootstrap } from './useCfoBootstrap'
import type { CfoNavProfile } from './cfoNav'
import { isCfoMobileConstrained, type CfoRuntimeProfile } from './cfoRuntimeProfile'

const SURFACE = CFO_SURFACES.explore
const EXPORT_ROW_LIMIT = EXCEL_WORKSHEET_ROW_LIMIT - 5
const EXPORT_FETCH_PAGE_SIZE = 100_000
const VIRTUAL_ROW_HEIGHT = 34
const VIRTUAL_OVERSCAN_ROWS = 24
const VIRTUAL_BLOCK_ROWS = 200
const MIN_VIRTUAL_FETCH_ROWS = 500
// 4 slots: profile-pane commits can burn two on a numeric range (gte + lte)
// while leaving room for a couple of categorical filters.
const MAX_FILTERS = 4
const SEARCH_QUERY_DEBOUNCE_MS = 180
const FILTER_OPERATORS: Array<{ value: CfoGlDetailFilterOperator; label: string; needsValue: boolean }> = [
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'not_equals', label: 'does not equal', needsValue: true },
  { value: 'starts_with', label: 'starts with', needsValue: true },
  { value: 'ends_with', label: 'ends with', needsValue: true },
  { value: 'gt', label: '>', needsValue: true },
  { value: 'gte', label: '>=', needsValue: true },
  { value: 'lt', label: '<', needsValue: true },
  { value: 'lte', label: '<=', needsValue: true },
  { value: 'blank', label: 'is blank', needsValue: false },
  { value: 'not_blank', label: 'is not blank', needsValue: false },
]

type DatePreset = 'all' | 'ytd' | 'last_12_months' | 'last_90_days' | 'custom'
const DEFAULT_DATE_PRESET: DatePreset = 'last_90_days'

export type CfoGlDetailExplorePageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  onNavSelect: (navId: string) => void
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

type FilterValueOption = {
  value: string
  count: number
}

type DateBounds = {
  minDate: string
  maxDate: string
}

type RowWindowResult = {
  rowOffset: number
  rows: Record<string, any>[]
}

type TotalsQueryRequest = {
  cacheEpoch: number
  cacheKey: string
  duckDbOpts?: Record<string, any> | null
  numericColumns: string[]
  sql: string
}

type RowWindowQueryRequest = {
  cacheEpoch: number
  cacheKey: string
  duckDbOpts?: Record<string, any> | null
  previewLimit: number
  rowOffset: number
  sql: string
}

const emptyTotals = (): CfoGlDetailTotals => ({ rowCount: 0, minDate: '', maxDate: '', totals: {} })

function setBoundedCache<K, V>(cache: Map<K, V>, key: K, value: V, limit = 24): void {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  while (cache.size > limit) {
    const first = cache.keys().next()
    if (first.done) break
    cache.delete(first.value)
  }
}

const defaultQueryState = (): CfoGlDetailQueryState => ({
  search: '',
  startDate: '',
  endDate: '',
  filters: [],
  sortColumn: 'txn_date',
  sortDirection: 'desc',
  visibleColumns: [...DEFAULT_GL_DETAIL_COLUMNS],
  page: 0,
  pageSize: 250,
})

function filterHasExecutablePredicate(filter: CfoGlDetailFilterState): boolean {
  if (!trimGlStr(filter.column)) return false
  if ((filter.mode || 'direct') === 'expression') {
    const operator = filter.operator || 'contains'
    return operator === 'blank' || operator === 'not_blank' || Boolean(trimGlStr(filter.expression))
  }
  return (filter.values || []).some((value) => Boolean(trimGlStr(value)))
}

function executableGlQueryState(state: CfoGlDetailQueryState): CfoGlDetailQueryState {
  return {
    ...state,
    filters: (state.filters || []).filter(filterHasExecutablePredicate),
    filter: state.filter && filterHasExecutablePredicate(state.filter) ? state.filter : undefined,
  }
}

function glQueryExecutionKey(state: CfoGlDetailQueryState): string {
  return JSON.stringify({
    search: state.search,
    startDate: state.startDate,
    endDate: state.endDate,
    filter: state.filter || null,
    filters: state.filters || [],
    scopes: state.scopes || [],
    rawPredicates: state.rawPredicates || [],
    sortColumn: state.sortColumn,
    sortDirection: state.sortDirection,
    visibleColumns: state.visibleColumns,
    page: state.page,
    pageSize: state.pageSize,
    offset: state.offset ?? null,
  })
}

function glQueryDataScopeKey(state: CfoGlDetailQueryState): string {
  return JSON.stringify({
    search: state.search,
    startDate: state.startDate,
    endDate: state.endDate,
    filter: state.filter || null,
    filters: state.filters || [],
    scopes: state.scopes || [],
    rawPredicates: state.rawPredicates || [],
    sortColumn: state.sortColumn,
    sortDirection: state.sortDirection,
    visibleColumns: state.visibleColumns,
  })
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms))

function downloadBase64Workbook(args: { fileName: string; contentType?: string | null; packageBase64: string }): void {
  if (typeof document === 'undefined') return
  const binary = atob(args.packageBase64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  const blob = new Blob([bytes], { type: args.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = args.fileName || 'cfo-core-gl-detail.xlsx'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function downloadTextFile(args: { fileName: string; contentType: string; text: string }): void {
  if (typeof document === 'undefined') return
  const blob = new Blob([args.text], { type: args.contentType })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = args.fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function csvEscape(value: unknown): string {
  const text = value == null ? '' : String(value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function rowsToCsvChunk(rows: Record<string, any>[], columns: string[], includeHeader: boolean): string {
  const lines: string[] = []
  if (includeHeader) lines.push(columns.map((column) => csvEscape(glDetailColumnLabel(column))).join(','))
  for (const row of rows) {
    lines.push(columns.map((column) => (
      GL_DETAIL_NUMERIC_COLUMNS.has(column)
        ? csvEscape(parseNumberish(row[column]))
        : csvEscape(formatGlCell(row[column], column))
    )).join(','))
  }
  return lines.join('\r\n')
}

function cacheHasReadyGl(cache: QuickBooksFpaCachePrepareResponse | null | undefined): boolean {
  if (!cache) return false
  if (cache.manifest?.gl_detail_ready || cache.manifest?.gl_detail_status === 'ready') return Boolean(findGlDetailCacheTable(cache))
  const table = findGlDetailCacheTable(cache)
  return Boolean(table && ((table.rows || []).length || table.parquet_url || (table.parquet_urls || []).length || table.artifact_uri || (table.artifact_uris || []).length))
}

function cacheWithHydratedGlTable(
  cache: QuickBooksFpaCachePrepareResponse,
  hydratedTable: NonNullable<ReturnType<typeof findGlDetailCacheTable>>,
): QuickBooksFpaCachePrepareResponse {
  const tables = [...(cache.tables || [])]
  const index = tables.findIndex((table) => cacheTableIdMatches(table, 'gl_detail'))
  if (index >= 0) tables[index] = { ...tables[index], ...hydratedTable }
  else tables.push(hydratedTable)
  return { ...cache, tables }
}

async function hydrateReadyGlCache(
  cache: QuickBooksFpaCachePrepareResponse,
  appApi: ServerApiClient,
  connectionId: string,
): Promise<QuickBooksFpaCachePrepareResponse | null> {
  if (!cacheHasReadyGl(cache)) return null
  const table = findGlDetailCacheTable(cache)
  if (!table) return null
  try {
    return cacheWithHydratedGlTable(cache, await hydrateGlDetailCacheTable(table, appApi, connectionId))
  } catch {
    // A stale prepared manifest can outlive the governed cache-file/export row.
    // Re-enter the supported prepare path instead of surfacing the file 404.
    return null
  }
}

async function loadPreparedGlCache(appApi: ServerApiClient, connectionId: string, onStatus: (status: string) => void): Promise<QuickBooksFpaCachePrepareResponse> {
  const status = typeof appApi.getQuickBooksFpaCacheStatus === 'function'
    ? await appApi.getQuickBooksFpaCacheStatus(connectionId)
    : null
  if (status?.manifest) {
    const cache = cacheManifestToPrepareResponse(status.manifest)
    const hydrated = await hydrateReadyGlCache(cache, appApi, connectionId)
    if (hydrated) return hydrated
  }
  onStatus('Preparing GL detail…')
  const prepared = await appApi.prepareQuickBooksFpaCache(connectionId, { includeGl: true, asyncDetail: true })
  const hydratedPrepared = await hydrateReadyGlCache(prepared, appApi, connectionId)
  if (hydratedPrepared) return hydratedPrepared
  for (let attempt = 0; attempt < 30; attempt += 1) {
    await sleep(attempt < 6 ? 1000 : 2000)
    const next = await appApi.getQuickBooksFpaCacheStatus(connectionId)
    const nextStatus = trimGlStr(next?.manifest?.gl_detail_status)
    onStatus(nextStatus === 'generating' ? 'Preparing GL detail…' : 'Checking GL detail…')
    if (next?.manifest?.gl_detail_status === 'failed') throw new Error('GL detail cache generation failed.')
    if (next?.manifest) {
      const cache = cacheManifestToPrepareResponse(next.manifest)
      const hydrated = await hydrateReadyGlCache(cache, appApi, connectionId)
      if (hydrated) return hydrated
    }
  }
  throw new Error('GL detail is still preparing. Refresh this page in a minute.')
}

function isGlDetailDisplayColumn(column: string): boolean {
  return !column.startsWith('__') && !/(^id$|_id$)/i.test(column)
}

function presetLabel(preset: DatePreset): string {
  if (preset === 'all') return 'All available'
  if (preset === 'ytd') return 'YTD'
  if (preset === 'last_12_months') return 'Last 12 months'
  if (preset === 'last_90_days') return 'Last 90 days'
  return 'Custom'
}

function presetDates(preset: DatePreset, bounds: DateBounds | CfoGlDetailTotals | null): { startDate: string; endDate: string } {
  const minDate = bounds?.minDate || ''
  const maxDate = bounds?.maxDate || new Date().toISOString().slice(0, 10)
  if (preset === 'all') return { startDate: '', endDate: '' }
  if (preset === 'custom') return { startDate: '', endDate: '' }
  const end = new Date(`${maxDate}T00:00:00Z`)
  if (Number.isNaN(end.getTime())) return { startDate: '', endDate: '' }
  const start = new Date(end)
  if (preset === 'ytd') {
    start.setUTCMonth(0, 1)
  } else if (preset === 'last_12_months') {
    start.setUTCMonth(start.getUTCMonth() - 11, 1)
  } else if (preset === 'last_90_days') {
    start.setUTCDate(start.getUTCDate() - 89)
  }
  return { startDate: start.toISOString().slice(0, 10), endDate: maxDate }
}

function buildFilterSummary(state: CfoGlDetailQueryState, rowCount: number): string {
  const parts = [`Rows: ${rowCount.toLocaleString()}`]
  if (state.startDate || state.endDate) parts.push(`Dates: ${state.startDate || '...'} to ${state.endDate || '...'}`)
  if (state.search) parts.push(`Search: ${state.search}`)
  for (const filter of state.filters || []) {
    if (!filter.column) continue
    if ((filter.mode || 'direct') === 'expression') {
      const expr = filter.operator === 'blank' || filter.operator === 'not_blank' ? filter.operator : `${filter.operator || 'contains'} ${filter.expression || ''}`
      parts.push(`${glDetailColumnLabel(filter.column)}: ${expr}`)
    } else if (filter.values.length) {
      parts.push(`${glDetailColumnLabel(filter.column)}: ${filter.values.join(', ')}`)
    }
  }
  return parts.join(' | ')
}

function emptyFilter(column = ''): CfoGlDetailFilterState {
  return { column, mode: 'direct', values: [], operator: 'contains', expression: '' }
}

function normalizedFilterSlots(filters: CfoGlDetailFilterState[] | undefined): CfoGlDetailFilterState[] {
  const active = (filters || []).filter((filter) => trimGlStr(filter.column)).slice(0, MAX_FILTERS)
  if (active.length < MAX_FILTERS) return [...active, emptyFilter()]
  return active
}

function CfoGlDetailAdvancedExplorePage(props: CfoGlDetailExplorePageProps) {
  const { appApi, workerClient = null, workbookId, onNavSelect, navProfile = 'full', runtimeProfile } = props
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { bootstrap, cacheState, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  const connectionId = trimGlStr(bootstrap?.connection_id)
  const accountingProvider = trimGlStr(bootstrap?.accounting_provider) || 'quickbooks'
  const analysis = useCfoAnalysis()
  const [queryState, setQueryState] = useState<CfoGlDetailQueryState>(() => defaultQueryState())
  const [queryExecutionState, setQueryExecutionState] = useState<CfoGlDetailQueryState>(() => defaultQueryState())
  const [datePreset, setDatePreset] = useState<DatePreset>(DEFAULT_DATE_PRESET)
  const [dateBounds, setDateBounds] = useState<DateBounds | null>(null)
  const [availableColumns, setAvailableColumns] = useState<string[]>([])
  const [rows, setRows] = useState<Record<string, any>[]>([])
  const [rowOffset, setRowOffset] = useState(0)
  const [scrollTop, setScrollTop] = useState(0)
  const [scrollViewportHeight, setScrollViewportHeight] = useState(520)
  const [totals, setTotals] = useState<CfoGlDetailTotals>(() => emptyTotals())
  const [filterOptionsByIndex, setFilterOptionsByIndex] = useState<Record<number, FilterValueOption[]>>({})
  const [filterOptionsLoadingByIndex, setFilterOptionsLoadingByIndex] = useState<Record<number, boolean>>({})
  const [filterValueSearchByIndex, setFilterValueSearchByIndex] = useState<Record<number, string>>({})
  const [filterValueSortByIndex, setFilterValueSortByIndex] = useState<Record<number, CfoGlDetailDistinctValueSort>>({})
  const [loadStatus, setLoadStatus] = useState('Checking GL detail…')
  const [loadError, setLoadError] = useState('')
  const [materialized, setMaterialized] = useState(false)
  const [queryBusyCount, setQueryBusyCount] = useState(0)
  const [exportBusy, setExportBusy] = useState(false)
  const [exportStatus, setExportStatus] = useState('')
  const [exportOverflowOpen, setExportOverflowOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [openMenu, setOpenMenu] = useState<'columns' | `filter-values-${number}` | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [profileFilters, setProfileFilters] = useState<CfoGlProfileFilters>({})
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const mountedRef = useRef(true)
  const queryCacheEpochRef = useRef(0)
  const filterOptionsCacheRef = useRef<Map<string, FilterValueOption[]>>(new Map())
  const filterOptionsInflightRef = useRef<Map<string, Promise<FilterValueOption[]>>>(new Map())
  const totalsCacheRef = useRef<Map<string, CfoGlDetailTotals>>(new Map())
  const totalsRunningRef = useRef(false)
  const pendingTotalsRequestRef = useRef<TotalsQueryRequest | null>(null)
  const latestTotalsKeyRef = useRef('')
  const latestTotalsScopeKeyRef = useRef('')
  const rowWindowCacheRef = useRef<Map<string, RowWindowResult>>(new Map())
  const rowWindowRunningRef = useRef(false)
  const pendingRowWindowRequestRef = useRef<RowWindowQueryRequest | null>(null)
  const latestRowWindowKeyRef = useRef('')
  const latestRowScopeKeyRef = useRef('')
  const [totalsPump, setTotalsPump] = useState(0)
  const [rowWindowPump, setRowWindowPump] = useState(0)
  const queryBusy = queryBusyCount > 0
  const beginQueryWork = useCallback(() => {
    setQueryBusyCount((count) => count + 1)
    let ended = false
    return () => {
      if (ended) return
      ended = true
      setQueryBusyCount((count) => Math.max(0, count - 1))
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const displayColumns = useMemo(() => availableColumns.filter(isGlDetailDisplayColumn), [availableColumns])
  const visibleColumns = useMemo(
    () => selectVisibleGlColumns(queryState.visibleColumns, displayColumns),
    [displayColumns, queryState.visibleColumns],
  )
  const visibleNumericColumns = useMemo(
    () => visibleColumns.filter((column) => GL_DETAIL_NUMERIC_COLUMNS.has(column)),
    [visibleColumns],
  )
  const exportOverflowSheetCount = useMemo(
    () => requiredCfoGlDetailExcelSheetCount(totals.rowCount),
    [totals.rowCount],
  )
  const filterSlots = useMemo(() => normalizedFilterSlots(queryState.filters), [queryState.filters])
  const selectedFilterColumns = useMemo(
    () => new Set((queryState.filters || []).map((filter) => filter.column).filter(Boolean)),
    [queryState.filters],
  )
  const virtualWindow = useMemo(() => {
    const firstVisibleRow = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_HEIGHT))
    const viewportRows = Math.max(1, Math.ceil(scrollViewportHeight / VIRTUAL_ROW_HEIGHT))
    const fetchStart = Math.max(0, Math.floor(Math.max(0, firstVisibleRow - VIRTUAL_OVERSCAN_ROWS) / VIRTUAL_BLOCK_ROWS) * VIRTUAL_BLOCK_ROWS)
    const fetchLimit = Math.max(MIN_VIRTUAL_FETCH_ROWS, viewportRows + VIRTUAL_OVERSCAN_ROWS * 2 + VIRTUAL_BLOCK_ROWS)
    return { fetchStart, fetchLimit }
  }, [scrollTop, scrollViewportHeight])
  const virtualFetchStart = virtualWindow.fetchStart
  const virtualFetchLimit = virtualWindow.fetchLimit

  const resetVirtualScroll = useCallback(() => {
    setRowOffset(0)
    setScrollTop(0)
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0
  }, [])

  // Inline GL opener for the shared analysis modal: this page IS the GL
  // explorer, so instead of a full-page navigation back to itself we apply the
  // account scope as a regular filter on the live table. Ref-backed so the
  // registered callback stays identity-stable while reading current state.
  const openGlDetailInlineRef = useRef<(target: CfoAnalysisGlDetailTarget) => boolean>(() => false)
  openGlDetailInlineRef.current = (target) => {
    if (!materialized) return false
    const accountId = trimGlStr(target.accountId)
    const label = trimGlStr(target.metricLabel)
    const filter: CfoGlDetailFilterState | null = accountId && availableColumns.includes('account_id')
      ? { column: 'account_id', mode: 'expression', values: [], operator: 'equals', expression: accountId }
      : label && availableColumns.includes('account_name')
        ? { column: 'account_name', mode: 'expression', values: [], operator: 'contains', expression: label }
        : label && availableColumns.includes('account')
          ? { column: 'account', mode: 'expression', values: [], operator: 'contains', expression: label }
          : null
    if (!filter) return false
    setQueryState((current) => ({ ...current, search: '', scopes: undefined, filters: [filter], page: 0 }))
    resetVirtualScroll()
    // Unlike the statement pages (which stack a scoped GL modal above the
    // analysis card), the filtered result here is the page itself — close the
    // analysis session so the table is actually visible.
    analysis.close()
    return true
  }
  const openGlDetailInline = useCallback(
    (target: CfoAnalysisGlDetailTarget) => openGlDetailInlineRef.current(target),
    [],
  )

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpenMenu(null)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  useEffect(() => {
    if (!isFullscreen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFullscreen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen])

  useEffect(() => {
    if (!tableScrollRef.current) return
    setScrollViewportHeight(tableScrollRef.current.clientHeight || 520)
  }, [isFullscreen, rows.length])

  useEffect(() => {
    let cancelled = false
    setMaterialized(false)
    setRows([])
    setTotals(emptyTotals())
    setAvailableColumns([])
    setFilterOptionsByIndex({})
    setFilterOptionsLoadingByIndex({})
    setFilterValueSearchByIndex({})
    setFilterValueSortByIndex({})
    queryCacheEpochRef.current += 1
    filterOptionsCacheRef.current.clear()
    filterOptionsInflightRef.current.clear()
    totalsCacheRef.current.clear()
    pendingTotalsRequestRef.current = null
    latestTotalsKeyRef.current = ''
    latestTotalsScopeKeyRef.current = ''
    rowWindowCacheRef.current.clear()
    pendingRowWindowRequestRef.current = null
    latestRowWindowKeyRef.current = ''
    latestRowScopeKeyRef.current = ''
    setDateBounds(null)
    setProfileFilters({})
    setQueryState((current) => ((current.rawPredicates || []).length ? { ...current, rawPredicates: [] } : current))
    resetVirtualScroll()
    setLoadError('')
    if (!connectionId || accountingProvider !== 'quickbooks' || !workerClient || !duckDbOpts) {
      setLoadStatus(!connectionId ? 'Waiting for accounting connection…' : accountingProvider !== 'quickbooks' ? 'Loading governed ledger…' : 'Waiting for local DuckDB…')
      return
    }
    setLoadStatus('Checking GL detail…')
    loadPreparedGlCache(appApi, connectionId, setLoadStatus)
      .then(async (cache) => {
        if (cancelled) return
        setLoadStatus('Loading GL detail into DuckDB…')
        if (typeof (workerClient as any).cache_reset_execution_scope === 'function') {
          await (workerClient as any).cache_reset_execution_scope(CFO_CORE_DUCKDB_DATASET_ID, {
            executionScopeId: duckDbOpts.executionScopeId,
          })
          if (cancelled) return
        }
        const loaded = await materializeQuickBooksGlDetailTable({
          workerClient,
          appApi,
          connectionId,
          cache,
          duckDbOpts,
        })
        if (cancelled) return
        const loadedDisplayColumns = loaded.columns.filter(isGlDetailDisplayColumn)
        let initialDates = { startDate: '', endDate: '' }
        if (loaded.columns.includes('txn_date')) {
          const boundsResult = await workerClient.duckdb_run_sql(buildCfoGlDetailTotalsSql({
            state: { ...defaultQueryState(), visibleColumns: loadedDisplayColumns, pageSize: 1 },
            availableColumns: loaded.columns,
            numericColumns: [],
          }), {
            ...(duckDbOpts || {}),
            previewRowLimit: 1,
            timeoutMs: 60000,
            caller: 'QuickBooksCfoCore:gl_detail_explore_date_bounds',
          })
          if (cancelled) return
          const boundsTotals = parseCfoGlDetailTotals(previewRowsAsObjects(boundsResult)[0] || {}, [])
          const nextBounds = { minDate: boundsTotals.minDate, maxDate: boundsTotals.maxDate }
          setDateBounds(nextBounds)
          initialDates = presetDates(DEFAULT_DATE_PRESET, nextBounds)
        }
        setAvailableColumns(loaded.columns)
        // A statement drilldown (e.g. Financials "Open in GL detail") can hand
        // off a query scope; it overrides the default date preset once.
        const handoff = peekPendingCfoGlDetailHandoff()
        if (handoff) {
          clearPendingCfoGlDetailHandoff()
          setDatePreset('custom')
        }
        const handoffPatch = handoff
          ? {
              search: handoff.search || '',
              startDate: handoff.startDate || '',
              endDate: handoff.endDate || '',
              filters: (handoff.filters || []).slice(0, MAX_FILTERS),
              scopes: handoff.scopes?.length ? handoff.scopes : undefined,
            }
          : initialDates
        setQueryState((current) => ({
          ...current,
          ...handoffPatch,
          visibleColumns: selectVisibleGlColumns(current.visibleColumns, loadedDisplayColumns).length
            ? selectVisibleGlColumns(current.visibleColumns, loadedDisplayColumns)
            : [...DEFAULT_GL_DETAIL_COLUMNS].filter((column) => loadedDisplayColumns.includes(column)),
          sortColumn: loadedDisplayColumns.includes(current.sortColumn) ? current.sortColumn : (loadedDisplayColumns.includes('txn_date') ? 'txn_date' : loadedDisplayColumns[0] || ''),
          page: 0,
        }))
        setMaterialized(true)
        setLoadStatus(`Loaded ${loaded.rowCountHint ? loaded.rowCountHint.toLocaleString() : 'available'} GL rows.`)
      })
      .catch((error: any) => {
        if (cancelled) return
        setLoadError(trimGlStr(error?.message || error) || 'GL detail failed to load.')
        setLoadStatus('GL detail failed.')
      })
    return () => {
      cancelled = true
    }
  }, [accountingProvider, appApi, connectionId, duckDbOpts, reloadKey, resetVirtualScroll, workerClient])

  const nextExecutionState = useMemo(() => executableGlQueryState(queryState), [queryState])
  const nextExecutionKey = useMemo(() => glQueryExecutionKey(nextExecutionState), [nextExecutionState])

  useEffect(() => {
    const applyNextExecution = () => {
      setQueryExecutionState((current) => (
        glQueryExecutionKey(current) === nextExecutionKey ? current : nextExecutionState
      ))
    }
    const delay = nextExecutionState.search !== queryExecutionState.search ? SEARCH_QUERY_DEBOUNCE_MS : 0
    if (delay <= 0) {
      applyNextExecution()
      return
    }
    const timer = window.setTimeout(applyNextExecution, delay)
    return () => window.clearTimeout(timer)
  }, [nextExecutionKey, nextExecutionState, queryExecutionState.search])

  const totalsNumericColumns = useMemo(
    () => Array.from(new Set([...visibleNumericColumns, ...Array.from(GL_DETAIL_NUMERIC_COLUMNS).filter((column) => availableColumns.includes(column))])),
    [availableColumns, visibleNumericColumns],
  )
  const totalsQuerySql = useMemo(() => {
    if (!materialized || !availableColumns.length) return ''
    return buildCfoGlDetailTotalsSql({
      state: queryExecutionState,
      availableColumns,
      numericColumns: totalsNumericColumns,
    })
  }, [availableColumns, materialized, queryExecutionState, totalsNumericColumns])
  const queryDataScopeKey = useMemo(() => glQueryDataScopeKey(queryExecutionState), [queryExecutionState])
  const virtualFetchStartForQuery = useMemo(() => {
    if (latestRowScopeKeyRef.current !== queryDataScopeKey) return 0
    if (totals.rowCount > 0 && virtualFetchStart >= totals.rowCount) return 0
    return virtualFetchStart
  }, [queryDataScopeKey, totals.rowCount, virtualFetchStart])
  const rowQuerySql = useMemo(() => {
    if (!materialized || !availableColumns.length) return ''
    return buildCfoGlDetailRowsSql({
      state: {
        ...queryExecutionState,
        page: 0,
        offset: virtualFetchStartForQuery,
        pageSize: virtualFetchLimit,
      },
      availableColumns,
    })
  }, [availableColumns, materialized, queryExecutionState, virtualFetchLimit, virtualFetchStartForQuery])

  useEffect(() => {
    if (!materialized || !workerClient || !availableColumns.length || !totalsQuerySql) return
    const cacheKey = `${totalsNumericColumns.join('|')}::${totalsQuerySql}`
    latestTotalsKeyRef.current = cacheKey
    const cached = totalsCacheRef.current.get(cacheKey)
    if (cached) {
      setTotals(cached)
      return
    }
    const scopeKey = queryDataScopeKey
    if (latestTotalsScopeKeyRef.current !== scopeKey) {
      latestTotalsScopeKeyRef.current = scopeKey
      setTotals(emptyTotals())
    }
    pendingTotalsRequestRef.current = {
      cacheEpoch: queryCacheEpochRef.current,
      cacheKey,
      duckDbOpts,
      numericColumns: totalsNumericColumns,
      sql: totalsQuerySql,
    }
    setTotalsPump((value) => value + 1)
  }, [availableColumns.length, duckDbOpts, materialized, queryDataScopeKey, totalsNumericColumns, totalsQuerySql, workerClient])

  useEffect(() => {
    if (!workerClient || totalsRunningRef.current) return
    const request = pendingTotalsRequestRef.current
    if (!request) return
    pendingTotalsRequestRef.current = null
    totalsRunningRef.current = true
    const endQueryWork = beginQueryWork()
    workerClient.duckdb_run_sql(request.sql, {
      ...(request.duckDbOpts || {}),
      interruptPrevious: true,
      previewRowLimit: 1,
      timeoutMs: 120000,
      caller: 'QuickBooksCfoCore:gl_detail_explore_totals',
    })
      .then((totalsResult: any) => {
        const totalsRow = previewRowsAsObjects(totalsResult)[0] || {}
        const nextTotals = parseCfoGlDetailTotals(totalsRow, request.numericColumns)
        if (queryCacheEpochRef.current === request.cacheEpoch) {
          setBoundedCache(totalsCacheRef.current, request.cacheKey, nextTotals)
        }
        if (mountedRef.current && latestTotalsKeyRef.current === request.cacheKey) setTotals(nextTotals)
      })
      .catch((error: any) => {
        if (mountedRef.current && latestTotalsKeyRef.current === request.cacheKey) {
          setLoadError(trimGlStr(error?.message || error) || 'GL detail totals query failed.')
        }
      })
      .finally(() => {
        totalsRunningRef.current = false
        endQueryWork()
        if (mountedRef.current && pendingTotalsRequestRef.current) setTotalsPump((value) => value + 1)
      })
  }, [beginQueryWork, totalsPump, workerClient])

  useEffect(() => {
    if (!materialized || !workerClient || !availableColumns.length || !rowQuerySql) return
    const cacheKey = `${virtualFetchStartForQuery}:${virtualFetchLimit}:${rowQuerySql}`
    latestRowWindowKeyRef.current = cacheKey
    const cached = rowWindowCacheRef.current.get(cacheKey)
    if (cached) {
      setRowOffset(cached.rowOffset)
      setRows(cached.rows)
      return
    }
    const scopeKey = queryDataScopeKey
    if (latestRowScopeKeyRef.current !== scopeKey) {
      latestRowScopeKeyRef.current = scopeKey
      setRowOffset(0)
      setRows([])
    }
    pendingRowWindowRequestRef.current = {
      cacheEpoch: queryCacheEpochRef.current,
      cacheKey,
      duckDbOpts,
      previewLimit: virtualFetchLimit,
      rowOffset: virtualFetchStartForQuery,
      sql: rowQuerySql,
    }
    setRowWindowPump((value) => value + 1)
  }, [availableColumns.length, duckDbOpts, materialized, queryDataScopeKey, rowQuerySql, virtualFetchLimit, virtualFetchStartForQuery, workerClient])

  useEffect(() => {
    if (!workerClient || rowWindowRunningRef.current) return
    const request = pendingRowWindowRequestRef.current
    if (!request) return
    pendingRowWindowRequestRef.current = null
    rowWindowRunningRef.current = true
    const endQueryWork = beginQueryWork()
    workerClient.duckdb_run_sql(request.sql, {
      ...(request.duckDbOpts || {}),
      interruptPrevious: true,
      previewRowLimit: request.previewLimit,
      timeoutMs: 120000,
      caller: 'QuickBooksCfoCore:gl_detail_explore_rows',
    })
      .then((rowsResult: any) => {
        const nextRows = { rowOffset: request.rowOffset, rows: previewRowsAsObjects(rowsResult) }
        if (queryCacheEpochRef.current === request.cacheEpoch) {
          setBoundedCache(rowWindowCacheRef.current, request.cacheKey, nextRows)
        }
        if (!mountedRef.current || latestRowWindowKeyRef.current !== request.cacheKey) return
        setRowOffset(nextRows.rowOffset)
        setRows(nextRows.rows)
      })
      .catch((error: any) => {
        if (mountedRef.current && latestRowWindowKeyRef.current === request.cacheKey) {
          setLoadError(trimGlStr(error?.message || error) || 'GL detail query failed.')
        }
      })
      .finally(() => {
        rowWindowRunningRef.current = false
        endQueryWork()
        if (mountedRef.current && pendingRowWindowRequestRef.current) setRowWindowPump((value) => value + 1)
      })
  }, [beginQueryWork, rowWindowPump, workerClient])

  useEffect(() => {
    let cancelled = false
    const match = typeof openMenu === 'string' ? openMenu.match(/^filter-values-(\d+)$/) : null
    const index = match ? Math.max(0, Math.floor(Number(match[1]) || 0)) : -1
    const filter = index >= 0 ? (queryState.filters || [])[index] : null
    if (!materialized || !workerClient || !availableColumns.length || !filter?.column || (filter.mode || 'direct') !== 'direct') {
      setFilterOptionsByIndex({})
      setFilterOptionsLoadingByIndex({})
      return
    }
    const sql = buildCfoGlDetailDistinctValuesSql({
      state: queryState,
      availableColumns,
      column: filter.column,
      limit: 100,
      filterIndex: index,
      search: filterValueSearchByIndex[index] || '',
      sort: filterValueSortByIndex[index] || 'count',
    })
    const cacheKey = `${index}:${filter.column}:${filterValueSearchByIndex[index] || ''}:${filterValueSortByIndex[index] || 'count'}:${sql}`
    const cached = filterOptionsCacheRef.current.get(cacheKey)
    if (cached) {
      setFilterOptionsByIndex((current) => ({ ...current, [index]: cached }))
      setFilterOptionsLoadingByIndex((current) => ({ ...current, [index]: false }))
      return
    }
    setFilterOptionsLoadingByIndex((current) => ({ ...current, [index]: true }))
    let promise = filterOptionsInflightRef.current.get(cacheKey)
    if (!promise) {
      promise = workerClient.duckdb_run_sql(sql, {
        ...(duckDbOpts || {}),
        interruptPrevious: true,
        previewRowLimit: 100,
        timeoutMs: 60000,
        caller: 'QuickBooksCfoCore:gl_detail_explore_filter_values',
      }).then((result: any) => {
        const next = previewRowsAsObjects(result).map((row) => ({
          value: String(row.value ?? ''),
          count: Math.max(0, Math.floor(Number(row.row_count || 0) || 0)),
        })).filter((option) => option.value)
        setBoundedCache(filterOptionsCacheRef.current, cacheKey, next)
        return next
      }).finally(() => {
        filterOptionsInflightRef.current.delete(cacheKey)
      })
      filterOptionsInflightRef.current.set(cacheKey, promise)
    }
    promise
      .then((next) => {
        if (cancelled) return
        setFilterOptionsByIndex((current) => ({ ...current, [index]: next }))
      })
      .catch(() => {
        if (!cancelled) setFilterOptionsByIndex((current) => ({ ...current, [index]: [] }))
      })
      .finally(() => {
        if (!cancelled) setFilterOptionsLoadingByIndex((current) => ({ ...current, [index]: false }))
      })
    return () => {
      cancelled = true
    }
  }, [availableColumns, duckDbOpts, filterValueSearchByIndex, filterValueSortByIndex, materialized, openMenu, queryState, workerClient])

  const patchQuery = useCallback((patch: Partial<CfoGlDetailQueryState>) => {
    setQueryState((current) => ({ ...current, ...patch, page: patch.page ?? 0 }))
    resetVirtualScroll()
  }, [resetVirtualScroll])

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset)
    const dates = preset === 'custom'
      ? { startDate: queryState.startDate, endDate: queryState.endDate }
      : presetDates(preset, dateBounds || totals)
    setQueryState((current) => ({ ...current, ...dates, page: 0 }))
    resetVirtualScroll()
  }

  // Card clicks filter directly and stack across cards: the selections are
  // compiled to SQL predicates carried on queryState.rawPredicates, so every
  // downstream query (rows, totals, filter values, export) respects them.
  const applyProfileFilters = useCallback((next: CfoGlProfileFilters) => {
    setProfileFilters(next)
    resetVirtualScroll()
    setQueryState((current) => ({ ...current, rawPredicates: cfoGlProfileFiltersToPredicates(next), page: 0 }))
  }, [resetVirtualScroll])

  const handleProfileClearColumnFilter = useCallback((column: string) => {
    resetVirtualScroll()
    const nextProfile = { ...profileFilters }
    delete nextProfile[column]
    setProfileFilters(nextProfile)
    setQueryState((current) => ({
      ...current,
      rawPredicates: cfoGlProfileFiltersToPredicates(nextProfile),
      filters: (current.filters || []).filter((filter) => filter.column !== column),
      page: 0,
    }))
  }, [profileFilters, resetVirtualScroll])

  const profileActiveFilterColumns = useMemo(
    () => new Set((queryState.filters || []).map((filter) => filter.column).filter(Boolean)),
    [queryState.filters],
  )

  const toggleColumn = (column: string) => {
    setQueryState((current) => {
      const next = current.visibleColumns.includes(column)
        ? current.visibleColumns.filter((item) => item !== column)
        : [...current.visibleColumns, column]
      return { ...current, visibleColumns: next.length ? next : [column], page: 0 }
    })
  }

  const updateFilterAt = (index: number, patch: Partial<CfoGlDetailFilterState>) => {
    const currentFilter = (queryState.filters || [])[index]
    const nextFilter = { ...(currentFilter || emptyFilter()), ...patch }
    const shouldResetRows = Boolean(currentFilter && filterHasExecutablePredicate(currentFilter)) || filterHasExecutablePredicate(nextFilter)
    if (shouldResetRows) resetVirtualScroll()
    if (patch.column !== undefined || patch.mode !== undefined) {
      setFilterValueSearchByIndex((current) => {
        const next = { ...current }
        delete next[index]
        return next
      })
    }
    setQueryState((current) => {
      const next = [...(current.filters || [])]
      const base = next[index] || emptyFilter()
      next[index] = { ...base, ...patch }
      const compact = next
        .slice(0, MAX_FILTERS)
        .filter((filter) => trimGlStr(filter.column))
        .map((filter) => ({
          ...filter,
          mode: filter.mode || 'direct',
          values: filter.mode === 'expression' ? [] : (filter.values || []),
          operator: filter.operator || 'contains',
          expression: filter.mode === 'direct' ? '' : (filter.expression || ''),
        }))
      return { ...current, filters: compact, page: 0 }
    })
  }

  const clearFilterAt = (index: number) => {
    resetVirtualScroll()
    setFilterValueSearchByIndex((current) => {
      const next = { ...current }
      delete next[index]
      return next
    })
    setQueryState((current) => ({
      ...current,
      filters: (current.filters || []).filter((_filter, filterIndex) => filterIndex !== index),
      page: 0,
    }))
  }

  const toggleFilterValue = (index: number, value: string) => {
    resetVirtualScroll()
    setQueryState((current) => {
      const next = [...(current.filters || [])]
      const base = next[index] || emptyFilter()
      const values = (base.values || []).includes(value)
        ? (base.values || []).filter((item) => item !== value)
        : [...(base.values || []), value]
      next[index] = { ...base, mode: 'direct', values }
      return { ...current, filters: next.filter((filter) => trimGlStr(filter.column)).slice(0, MAX_FILTERS), page: 0 }
    })
  }

  const sortBy = (column: string) => {
    setQueryState((current) => ({
      ...current,
      sortColumn: column,
      sortDirection: current.sortColumn === column && current.sortDirection === 'desc' ? 'asc' : 'desc',
      page: 0,
    }))
    resetVirtualScroll()
  }

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget
    setScrollTop(target.scrollTop)
    setScrollViewportHeight(target.clientHeight || 520)
  }

  const fetchFilteredExportRows = async (args?: { onRows?: (rows: Record<string, any>[], offset: number) => void }): Promise<Record<string, any>[]> => {
    if (typeof workerClient?.duckdb_run_sql !== 'function') throw new Error('GL detail query export is not available in this session.')
    const collectRows = typeof args?.onRows !== 'function'
    const allRows: Record<string, any>[] = []
    const totalRows = Math.max(0, Math.floor(totals.rowCount))
    let offset = 0
    while (offset < totalRows) {
      const remaining = totalRows - offset
      const pageSize = Math.max(1, Math.min(EXPORT_FETCH_PAGE_SIZE, remaining))
      const exportState: CfoGlDetailQueryState = {
        ...queryState,
        page: 0,
        offset,
        pageSize,
      }
      const result = await workerClient.duckdb_run_sql(buildCfoGlDetailRowsSql({
        state: exportState,
        availableColumns,
        maxPageSize: pageSize,
      }), {
        ...(duckDbOpts || {}),
        previewRowLimit: pageSize,
        timeoutMs: 180000,
        caller: 'QuickBooksCfoCore:gl_detail_explore_export_rows',
      })
      const pageRows = previewRowsAsObjects(result)
      if (!pageRows.length) break
      args?.onRows?.(pageRows, offset)
      if (collectRows) allRows.push(...pageRows)
      offset += pageRows.length
      setExportStatus(`Exporting GL detail… ${Math.min(offset, totalRows).toLocaleString()} of ${totalRows.toLocaleString()} rows prepared.`)
      if (pageRows.length < pageSize) break
      await Promise.resolve()
    }
    return allRows
  }

  const exportFilteredCsv = async () => {
    if (typeof workerClient?.duckdb_run_sql !== 'function') {
      setExportStatus('CSV export is not available in this session.')
      return
    }
    setExportOverflowOpen(false)
    setExportBusy(true)
    setExportStatus('Exporting GL detail to CSV…')
    try {
      const chunks: string[] = []
      let exportedRows = 0
      await fetchFilteredExportRows({
        onRows: (pageRows) => {
          chunks.push(rowsToCsvChunk(pageRows, visibleColumns, exportedRows === 0))
          exportedRows += pageRows.length
        },
      })
      if (!chunks.length) chunks.push(rowsToCsvChunk([], visibleColumns, true))
      downloadTextFile({
        fileName: buildCfoGlDetailCsvFileName(bootstrap?.company_name),
        contentType: 'text/csv;charset=utf-8',
        text: chunks.join('\r\n'),
      })
      setExportStatus(`Exported ${exportedRows.toLocaleString()} GL rows to CSV.`)
    } catch (error: any) {
      setExportStatus(`CSV export failed: ${trimGlStr(error?.message || error) || 'Unknown error'}`)
    } finally {
      setExportBusy(false)
    }
  }

  const exportFilteredExcelWorkbook = async (splitAcrossSheets: boolean) => {
    if (!workerClient?.export_excel_snapshot || typeof workerClient.duckdb_run_sql !== 'function') {
      setExportStatus('Excel export is not available in this session.')
      return
    }
    setExportOverflowOpen(false)
    setExportBusy(true)
    setExportStatus(splitAcrossSheets ? 'Exporting GL detail across workbook tabs…' : 'Exporting GL detail…')
    try {
      const exportRows = await fetchFilteredExportRows()
      const fileName = buildCfoGlDetailExcelFileName(bootstrap?.company_name)
      const snapshot = buildCfoGlDetailExcelSnapshot({
        companyName: bootstrap?.company_name,
        rows: exportRows,
        columns: visibleColumns,
        totals,
        filtersLabel: [
          buildFilterSummary(queryState, totals.rowCount),
          cfoGlProfileFiltersLabel(profileFilters, glDetailColumnLabel),
        ].filter(Boolean).join(' | '),
        splitAcrossSheets,
      })
      const response = await workerClient.export_excel_snapshot({
        format: 'xlsx',
        file_name: fileName,
        include_calculated_values: true,
        snapshot,
        actor_client_id: 'cfo_core_gl_detail_export',
        created_at_ms: Date.now(),
      })
      downloadBase64Workbook({
        fileName: response.file_name || fileName,
        contentType: response.content_type,
        packageBase64: response.package_base64,
      })
      setExportStatus(splitAcrossSheets
        ? `Exported ${response.file_name || fileName} across ${snapshot.sheets.length.toLocaleString()} tabs.`
        : `Exported ${response.file_name || fileName}.`)
    } catch (error: any) {
      setExportStatus(`Excel export failed: ${trimGlStr(error?.message || error) || 'Unknown error'}`)
    } finally {
      setExportBusy(false)
    }
  }

  // When the local engine cannot serve this page at all — the materialization
  // failed, or there is no DuckDB workspace in this session — degrade to the
  // simplified server-backed GL browser instead of a dead status panel. A
  // missing connection is excluded: the server contract needs the same binding.
  const missingConnection = !bootstrapLoading && !connectionId
  const localEngineUnavailable = !bootstrapLoading && Boolean(connectionId) && (accountingProvider !== 'quickbooks' || !workerClient || !duckDbOpts)
  const showServerFallback = !materialized && (Boolean(loadError) || localEngineUnavailable)
  // Never surface raw internal engine/worker errors (e.g. "HOST_CALL received but
  // not running in a native desktop shell", DuckDB/WASM/OPFS/parquet plumbing) on
  // the customer surface. The raw `loadError` is still logged for telemetry.
  const humanGlLoadError = (() => {
    const text = trimGlStr(loadError)
    if (!text) return ''
    if (/HOST_CALL|Tauri|Electron|native desktop shell|duckdb|wasm|opfs|parquet|worker/i.test(text)) {
      return 'The full ledger view could not load in the browser, so this shows the server ledger view instead.'
    }
    return text
  })()
  const firstPaintStatusDetail = !connectionId
    ? 'No accounting connection is currently bound to this workbook, so transaction-level GL detail cannot load yet.'
    : accountingProvider !== 'quickbooks'
      ? 'Search, filter, and inspect the reconciled provider-neutral general ledger.'
    : !workerClient
      ? 'The local DuckDB workspace is not available in this browser session. Use the governed summary now or reopen the desktop session.'
      : 'The full GL table is still preparing; keep working while the bounded detail cache finishes.'

  const glRowContext = useCallback((row: Record<string, any>, rowIndex: number) => {
    const accountLabel = trimGlStr(row.account_name || row.account || row.account_fully_qualified_name) || 'GL row'
    const amount = Number(row.amount ?? row.net_amount ?? row.debit ?? row.credit)
    const amountDisplay = Number.isFinite(amount) ? formatGlNumber(amount) : trimGlStr(row.amount_display || row.amount) || 'Selected'
    const metricId = trimGlStr(row.account_id || accountLabel) || null
    const rowKey = trimGlStr(row.id || row.transaction_id || row.txn_id || row.num) || `gl-row-${rowOffset + rowIndex}`
    return {
      interaction: 'evidence_click',
      source_component: 'gl_detail_row',
      metric_id: metricId,
      metric_label: accountLabel,
      source_tables: [accountingProvider === 'quickbooks' ? 'fg_qbo_fpa_gl_detail' : 'fg_general_ledger_accrual'],
      selection_key: rowKey,
      row_key: rowKey,
      value: Number.isFinite(amount) ? amount : null,
      display: amountDisplay,
      account_id: trimGlStr(row.account_id) || null,
      account_name: accountLabel,
      customer_id: trimGlStr(row.customer_id || row.customer) || null,
      transaction_id: trimGlStr(row.transaction_id || row.txn_id || row.source_id) || null,
      transaction_date: trimGlStr(row.txn_date || row.transaction_date || row.date) || null,
      source_fingerprint: `gl-detail:${rowKey}:${amountDisplay}`,
      deterministic_read: `${accountLabel} has a selected GL detail row for ${amountDisplay}.`,
      instant_card_insights: [`This GL row should be read as transaction-level evidence for ${accountLabel}, not as a period trend by itself.`],
      instant_card_prescriptive_actions: ['Use this row to open bounded transaction/account detail before changing the monthly narrative.'],
      instant_card_next_steps: [
        {
          kind: 'transactions',
          label: 'Open account detail',
          prompt: `Open bounded GL detail for ${accountLabel}.`,
          target: { surface: 'cfo_gl_detail', metricId, accountId: trimGlStr(row.account_id) || null },
        },
      ],
      instant_card_change_explanations: [
        {
          periodKind: 'current_month',
          label: 'Selected row',
          status: 'partial',
          current: amountDisplay,
          prior: '-',
          change: amountDisplay,
          headline: `${accountLabel} includes this selected transaction-level row for ${amountDisplay}.`,
          drivers: [
            {
              id: trimGlStr(row.transaction_id || row.txn_id || row.source_id) || rowKey,
              label: trimGlStr(row.name || row.vendor || row.customer || row.memo || accountLabel) || accountLabel,
              value: amountDisplay,
              direction: Number.isFinite(amount) && amount < 0 ? 'down' : Number.isFinite(amount) && amount > 0 ? 'up' : 'flat',
              category: 'transaction',
              sourceSurface: 'cfo_gl_detail',
              target: { surface: 'cfo_gl_detail', metricId, accountId: trimGlStr(row.account_id) || null },
            },
          ],
          confidence: 'medium',
          caveats: ['A selected GL row is transaction evidence; period comparability requires an account or category rollup.'],
          recommendedAction: 'Inspect the account rollup before treating this transaction as recurring.',
        },
      ],
    }
  }, [accountingProvider, rowOffset])
  const exportFilteredExcel = async () => {
    if (totals.rowCount > EXPORT_ROW_LIMIT) {
      setExportStatus(`${totals.rowCount.toLocaleString()} GL rows exceed Excel's ${EXCEL_WORKSHEET_ROW_LIMIT.toLocaleString()} rows-per-tab limit. Choose how to continue.`)
      setExportOverflowOpen(true)
      setOpenMenu(null)
      return
    }
    await exportFilteredExcelWorkbook(false)
  }

  const focusGlFilters = () => {
    setExportOverflowOpen(false)
    setExportStatus(`Narrow the GL filters to ${EXPORT_ROW_LIMIT.toLocaleString()} rows or fewer for a single Excel tab.`)
    window.setTimeout(() => {
      const target = menuRef.current?.querySelector<HTMLSelectElement>('select[aria-label="Filter 1 field"]')
        || menuRef.current?.querySelector<HTMLInputElement>('.cfo-gl-search input')
      target?.focus()
    }, 0)
  }

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="gl_detail"
      activeSection={SURFACE}
      eyebrow="Explore"
      title="GL detail"
      lede="Search and filter the reconciled general ledger for this workbook."
      crumb={<><b>Explore</b> · GL detail</>}
      asOfLabel={totals.maxDate ? `Through ${totals.maxDate}` : 'All available'}
      cacheState={cacheState}
      pageLoading={bootstrapLoading || (!missingConnection && !materialized && !loadError && !showServerFallback)}
      pageLoadingLabel={loadStatus}
      copilotSubtitle="GL detail analysis"
      copilotPlaceholder="Ask about the filtered ledger…"
      copilotIntro="You’re on GL detail. Ask about the visible filters, totals, accounts, vendors, or transaction patterns."
      askPlaceholder="Ask about this GL detail view…"
      suggestedPrompts={['Explain the largest debit and credit movements', 'Find unusual vendors in this date range', 'Summarize activity for the filtered account']}
      sourceTables={[accountingProvider === 'quickbooks' ? 'fg_qbo_fpa_gl_detail' : 'fg_general_ledger_accrual']}
      analysisSelection={{ page: 'gl_detail', query_state: queryState, visible_columns: visibleColumns, totals }}
      pageFactsFingerprint={`gl-detail:${rowOffset}:${rows.length}:${totals.rowCount}:${totals.maxDate || ''}`}
      openGlDetail={openGlDetailInline}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {() => {
        return (
        <div className={isFullscreen ? 'cfo-gl-page cfo-gl-page-fullscreen' : 'cfo-gl-page'}>
          {missingConnection ? (
            <section className="cfo-gl-state rise" role="status">
              <strong>GL detail needs an accounting connection.</strong>
              <span>{firstPaintStatusDetail}</span>
            </section>
          ) : null}

          {loadError && !showServerFallback && materialized ? (
            <section className="cfo-gl-state rise" role="alert">
              <strong>GL detail failed to load.</strong>
              <span>{humanGlLoadError}</span>
              <button type="button" className="cfo-shell-chip" onClick={() => setReloadKey((value) => value + 1)}>
                <RefreshCw size={15} /> Retry
              </button>
            </section>
          ) : null}

          {showServerFallback ? (
            <CfoGlDetailServerFallback
              appApi={appApi}
              workbookId={workbookId}
              reason={accountingProvider === 'quickbooks' ? 'local_unavailable' : 'canonical'}
              detail={humanGlLoadError || firstPaintStatusDetail}
              // Only offered when the local engine exists but failed. The
              // mobile fallback below deliberately omits it: there the full
              // explorer cannot run at all, so retrying would just fail again.
              onRetryLocal={accountingProvider === 'quickbooks' && loadError ? () => setReloadKey((value) => value + 1) : null}
            />
          ) : null}


          {materialized ? (
          <>
          <div className="cfo-gl-control-zone" ref={menuRef}>
            <CfoControlRow>
              <label className="cfo-gl-search">
                <Search size={16} />
                <input
                  value={queryState.search}
                  onChange={(event) => patchQuery({ search: event.target.value })}
                  placeholder="Search GL detail"
                />
              </label>
              <label className="cfo-gl-date-preset-select">
                <Calendar size={15} />
                <select value={datePreset} onChange={(event) => handlePresetChange(event.target.value as DatePreset)} aria-label="Date range preset">
                  {(['last_90_days', 'ytd', 'last_12_months', 'all', 'custom'] as DatePreset[]).map((preset) => (
                    <option key={preset} value={preset}>{presetLabel(preset)}</option>
                  ))}
                </select>
              </label>
              <label className="cfo-gl-date-input">
                <Calendar size={15} />
                <input
                  type="date"
                  value={queryState.startDate}
                  onChange={(event) => {
                    setDatePreset('custom')
                    patchQuery({ startDate: event.target.value })
                  }}
                />
              </label>
              <label className="cfo-gl-date-input">
                <Calendar size={15} />
                <input
                  type="date"
                  value={queryState.endDate}
                  onChange={(event) => {
                    setDatePreset('custom')
                    patchQuery({ endDate: event.target.value })
                  }}
                />
              </label>
              <div className="cfo-gl-menu">
                <button type="button" className="cfo-gl-menu-trigger" onClick={() => setOpenMenu((current) => current === 'columns' ? null : 'columns')}>
                  <Columns3 size={15} /> Columns
                </button>
                {openMenu === 'columns' ? (
                  <div className="cfo-gl-menu-panel">
                    {displayColumns.map((column) => (
                      <label key={column}>
                        <input type="checkbox" checked={visibleColumns.includes(column)} onChange={() => toggleColumn(column)} />
                        <span>{glDetailColumnLabel(column)}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
              <button type="button" className="cfo-shell-chip" onClick={exportFilteredExcel} disabled={exportBusy || queryBusy || !materialized}>
                <Download size={15} /> {exportBusy ? 'Exporting' : 'Excel'}
              </button>
              <button
                type="button"
                className="cfo-shell-chip"
                onClick={() => setIsFullscreen((value) => !value)}
                aria-pressed={isFullscreen}
              >
                {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                {isFullscreen ? 'Exit full screen' : 'Full screen'}
              </button>
            </CfoControlRow>
            <div className="cfo-gl-filter-row rise" aria-label="GL detail filters">
              <span className="cfo-gl-filter-row-label"><Filter size={15} /> Filters</span>
              {filterSlots.map((filter, index) => {
                const mode = filter.mode || 'direct'
                const operator = filter.operator || 'contains'
                const operatorMeta = FILTER_OPERATORS.find((item) => item.value === operator) || FILTER_OPERATORS[0]
                const options = filterOptionsByIndex[index] || []
                const valuesLoading = Boolean(filterOptionsLoadingByIndex[index])
                const selectedValues = filter.values || []
                const valueSearch = filterValueSearchByIndex[index] || ''
                const valueSort = filterValueSortByIndex[index] || 'count'
                return (
                  <div className="cfo-gl-filter-slot" key={`${index}-${filter.column || 'empty'}`}>
                    <select
                      value={filter.column}
                      aria-label={`Filter ${index + 1} field`}
                      onChange={(event) => updateFilterAt(index, {
                        column: event.target.value,
                        mode: 'direct',
                        values: [],
                        operator: 'contains',
                        expression: '',
                      })}
                    >
                      <option value="">{index === 0 ? 'Add filter' : `Add filter ${index + 1}`}</option>
                      {displayColumns.filter((column) => !selectedFilterColumns.has(column) || column === filter.column).map((column) => (
                        <option key={column} value={column}>{glDetailColumnLabel(column)}</option>
                      ))}
                    </select>
                    {filter.column ? (
                      <>
                        <select
                          value={mode === 'expression' ? 'expression' : filter.negate ? 'exclude' : 'direct'}
                          aria-label={`Filter ${index + 1} mode`}
                          onChange={(event) => {
                            const nextMode = event.target.value
                            if (nextMode === 'expression') {
                              updateFilterAt(index, { mode: 'expression', negate: false, values: [], expression: '', operator: 'contains' })
                              return
                            }
                            // direct <-> exclude keeps the chosen values and just flips the polarity
                            updateFilterAt(index, { mode: 'direct', negate: nextMode === 'exclude', expression: '', operator: 'contains' })
                          }}
                        >
                          <option value="direct">Select values</option>
                          <option value="exclude">Exclude values</option>
                          <option value="expression">Expression</option>
                        </select>
                        {mode === 'direct' ? (
                          <div className="cfo-gl-menu">
                            <button
                              type="button"
                              className="cfo-gl-menu-trigger cfo-gl-filter-values-trigger"
                              onClick={() => setOpenMenu((current) => current === `filter-values-${index}` ? null : `filter-values-${index}`)}
                            >
                              {valuesLoading ? <span className="cfo-gl-inline-spinner" aria-hidden="true" /> : null}
                              {selectedValues.length ? `${selectedValues.length} ${filter.negate ? 'excluded' : 'selected'}` : 'Choose values'}
                            </button>
                            {openMenu === `filter-values-${index}` ? (
                              <div className="cfo-gl-menu-panel cfo-gl-filter-values-panel">
                                <label className="cfo-gl-filter-values-search">
                                  <Search size={15} />
                                  <input
                                    value={valueSearch}
                                    onChange={(event) => setFilterValueSearchByIndex((current) => ({ ...current, [index]: event.target.value }))}
                                    placeholder="Search values"
                                    aria-label={`Search filter ${index + 1} values`}
                                  />
                                </label>
                                <div className="cfo-gl-filter-values-sort" role="group" aria-label={`Sort filter ${index + 1} values`}>
                                  <span className="cfo-gl-filter-values-sort-spacer" aria-hidden="true" />
                                  <button
                                    type="button"
                                    className="cfo-gl-filter-values-sort-value"
                                    aria-pressed={valueSort === 'value'}
                                    onClick={() => setFilterValueSortByIndex((current) => ({ ...current, [index]: 'value' }))}
                                  >
                                    <span>Value</span>
                                    <em>{valueSort === 'value' ? 'A-Z' : 'sort A-Z'}</em>
                                  </button>
                                  <button
                                    type="button"
                                    className="cfo-gl-filter-values-sort-count"
                                    aria-pressed={valueSort === 'count'}
                                    onClick={() => setFilterValueSortByIndex((current) => ({ ...current, [index]: 'count' }))}
                                  >
                                    <span>Count</span>
                                    <em>{valueSort === 'count' ? 'sorted' : 'sort'}</em>
                                  </button>
                                </div>
                                {valuesLoading ? (
                                  <div className="cfo-gl-menu-empty cfo-gl-menu-loading" role="status">
                                    <span className="cfo-gl-inline-spinner" aria-hidden="true" />
                                    <span>Loading values...</span>
                                  </div>
                                ) : options.length ? options.map((option) => (
                                  <label key={option.value}>
                                    <input
                                      type="checkbox"
                                      checked={selectedValues.includes(option.value)}
                                      onChange={() => toggleFilterValue(index, option.value)}
                                    />
                                    <span>{option.value}</span>
                                    <em>{option.count.toLocaleString()}</em>
                                  </label>
                                )) : <div className="cfo-gl-menu-empty">No values found</div>}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <>
                            <select
                              value={operator}
                              aria-label={`Filter ${index + 1} operator`}
                              onChange={(event) => updateFilterAt(index, { operator: event.target.value as CfoGlDetailFilterOperator })}
                            >
                              {FILTER_OPERATORS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                            </select>
                            {operatorMeta.needsValue ? (
                              <input
                                value={filter.expression || ''}
                                onChange={(event) => updateFilterAt(index, { expression: event.target.value })}
                                placeholder="Value or expression"
                              />
                            ) : null}
                          </>
                        )}
                        <button type="button" className="cfo-gl-filter-clear" onClick={() => clearFilterAt(index)} aria-label={`Clear filter ${index + 1}`}>
                          Clear
                        </button>
                      </>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>

          <CfoGlProfilePane
            workerClient={workerClient}
            duckDbOpts={duckDbOpts}
            availableColumns={availableColumns}
            visibleColumns={visibleColumns}
            queryState={queryExecutionState}
            totals={totals}
            totalsNumericColumns={visibleNumericColumns}
            profileFilters={profileFilters}
            onProfileFiltersChange={applyProfileFilters}
            activeFilterColumns={profileActiveFilterColumns}
            onClearColumnFilter={handleProfileClearColumnFilter}
          />

          {exportOverflowOpen ? (
            <section className="cfo-gl-export-overflow rise" role="dialog" aria-modal="false" aria-label="GL detail export size options">
              <div className="cfo-gl-export-overflow-copy">
                <strong>Filtered GL detail is too large for one Excel tab.</strong>
                <span>
                  {totals.rowCount.toLocaleString()} rows exceed the {EXCEL_WORKSHEET_ROW_LIMIT.toLocaleString()} row cap for a worksheet.
                </span>
              </div>
              <div className="cfo-gl-export-overflow-actions">
                <button type="button" className="cfo-shell-chip" onClick={focusGlFilters} disabled={exportBusy}>
                  <Filter size={15} /> Change filters
                </button>
                <button type="button" className="cfo-shell-chip" onClick={() => exportFilteredExcelWorkbook(true)} disabled={exportBusy}>
                  <TableProperties size={15} /> Split into {exportOverflowSheetCount.toLocaleString()} tabs
                </button>
                <button type="button" className="cfo-shell-chip" onClick={exportFilteredCsv} disabled={exportBusy}>
                  <FileText size={15} /> Export CSV
                </button>
              </div>
              <p>
                A split workbook keeps the file in Excel. CSV avoids worksheet row caps and works well as an Excel data connection source.
              </p>
              <button type="button" className="cfo-gl-export-overflow-close" aria-label="Dismiss export options" onClick={() => setExportOverflowOpen(false)} disabled={exportBusy}>
                Dismiss
              </button>
            </section>
          ) : null}
          {exportStatus ? <div className="cfo-gl-status rise" role="status">{exportStatus}</div> : null}
          {queryBusy && materialized ? <CfoLoadingNote compact label="Refreshing GL detail…" /> : null}

          <section className="cfo-gl-table-shell rise">
            <div className="cfo-gl-table-scroll" ref={tableScrollRef} onScroll={handleScroll}>
              <table className="cfo-gl-table">
                <thead>
                  <tr>
                    {visibleColumns.map((column) => (
                      <th key={column} className={GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'num' : undefined}>
                        <button type="button" onClick={() => sortBy(column)}>
                          {glDetailColumnLabel(column)}
                          {queryState.sortColumn === column ? <span>{queryState.sortDirection === 'asc' ? '↑' : '↓'}</span> : null}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length && rowOffset > 0 ? (
                    <tr className="cfo-gl-spacer-row" aria-hidden="true" style={{ height: rowOffset * VIRTUAL_ROW_HEIGHT }}>
                      <td colSpan={Math.max(1, visibleColumns.length)} />
                    </tr>
                  ) : null}
                  {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={`${row.txn_date || ''}-${row.num || ''}-${row.account_id || ''}-${rowIndex}`}>
                      {visibleColumns.map((column) => (
                        <td key={column} className={GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'num' : undefined}>
                          {formatGlCell(row[column], column)}
                        </td>
                      ))}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={Math.max(1, visibleColumns.length)} className="cfo-gl-empty">
                        {materialized ? 'No GL rows match the current filters.' : loadStatus}
                      </td>
                    </tr>
                  )}
                  {rows.length && totals.rowCount > rowOffset + rows.length ? (
                    <tr className="cfo-gl-spacer-row" aria-hidden="true" style={{ height: (totals.rowCount - rowOffset - rows.length) * VIRTUAL_ROW_HEIGHT }}>
                      <td colSpan={Math.max(1, visibleColumns.length)} />
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <footer className="cfo-gl-scroll-footer">
              <span>{totals.rowCount.toLocaleString()} filtered rows</span>
              <span>Showing {rows.length ? `${(rowOffset + 1).toLocaleString()}-${Math.min(totals.rowCount, rowOffset + rows.length).toLocaleString()}` : '0'} in viewport</span>
            </footer>
          </section>
          </>
          ) : null}
        </div>
        )
      }}
    </CfoInteriorPage>
  )
}

// The GL detail explorer streams and filters the full transaction table in local DuckDB,
// which needs a desktop browser. On a mobile-constrained runtime we can't run that engine,
// so we serve the simplified server-backed GL browser (governed compact gl-summary
// contract) instead: search + month scope + the first matching rows with totals.
function CfoGlDetailMobileFallback(props: Omit<CfoGlDetailExplorePageProps, 'workerClient'>) {
  const { appApi, workbookId, onNavSelect, navProfile = 'full', runtimeProfile } = props
  const { cacheState, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  // One-shot scope handed off from a statement drilldown (e.g. Financials
  // "Open in GL detail"). Peek in the initializer (StrictMode double-runs it,
  // so it must be side-effect free) and clear once mounted.
  const [handoff] = useState(() => peekPendingCfoGlDetailHandoff())
  useEffect(() => {
    if (handoff) clearPendingCfoGlDetailHandoff()
  }, [handoff])
  const initialSearch = handoff
    ? handoff.search || trimGlStr(handoff.filters?.[0]?.expression) || handoff.label || ''
    : ''
  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={null}
      workbookId={workbookId}
      navId="gl_detail"
      activeSection={SURFACE}
      eyebrow="Explore"
      title="GL detail"
      lede="Search the governed general ledger for this workbook."
      crumb={<><b>Explore</b> · GL detail</>}
      asOfLabel="—"
      cacheState={cacheState}
      pageLoading={bootstrapLoading}
      pageLoadingLabel="Loading…"
      copilotSubtitle="GL detail"
      copilotPlaceholder="Ask about GL detail…"
      askPlaceholder="Ask about GL detail…"
      duckDbOpts={null}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {() => (
        <CfoGlDetailServerFallback
          appApi={appApi}
          workbookId={workbookId}
          reason="mobile"
          initialSearch={initialSearch}
          initialMonth={handoff?.startDate || null}
        />
      )}
    </CfoInteriorPage>
  )
}

export function CfoGlDetailExplorePage(props: CfoGlDetailExplorePageProps) {
  if (isCfoMobileConstrained(props.runtimeProfile)) {
    return (
      <CfoGlDetailMobileFallback
        appApi={props.appApi}
        workbookId={props.workbookId}
        onNavSelect={props.onNavSelect}
        navProfile={props.navProfile}
        runtimeProfile={props.runtimeProfile}
      />
    )
  }
  return <CfoGlDetailAdvancedExplorePage {...props} />
}

export default CfoGlDetailExplorePage
